<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!isLoading"
      class="m-t-10 m-b-15"
    >
      <button
        data-testid="add-integration-button"
        type="button"
        class="btn btn-success btn-sm m-r-5"
        :disabled="isAddFormOpen"
        @click="showAddForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'PLATFORMS_ADD_BUTTON'" />
      </button>
    </div>
    <!-- Table Container -->
    <div
      v-if="!isLoading"
      class="grid_table_holder"
    >
      <v-table
        :data="integrations"
        :current-page.sync="currentPage"
        :filters="filters"
        :page-size="20"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              default-sort="asc"
              sort-key="name"
              v-t="'PLATFORM_NAME_LABEL'"
            />
            <v-th
              sort-key="type"
              v-t="'PLATFORMS_TYPE'"
            />
            <v-th
              sort-key="type"
              v-t="'PLATFORMS_STATUS'"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >

          <tr v-if="!integrations.length">
            <td colspan="100" v-t="'PLATFORMS_HEADER_NONE'"></td>
          </tr>
          <!-- Create New Form -->
          <tr
            v-if="isAddFormOpen"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <PlatformForm
                @closeForm="hideAddForm"
              />
            </td>
          </tr>

          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{
                'bg-row-odd': index % 2 === 0,
                'bg-row-even': index % 2 !== 0,
              }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <!-- Name -->
              <td>
                <span
                  class="clickable"
                  @click="showEditForm(row.id)"
                >
                  {{ row.name }}
                </span>
              </td>

              <!-- Type -->
              <td>
                {{ transformPlatformType(row.type) }}
              </td>

              <!-- Status -->
              <td>
                {{ row.status }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <PlatformActions
                  :integration="row"
                  :disabled="isDisabled"
                  :integration-to-reset="integrationToReset"
                  @deleteIntegration="showDeleteModal"
                  @refreshIntegration="refreshIntegration"
                  @editIntegration="showEditForm(row.id)"
                  @toggleEnabled="toggleIntegrationEnabled"
                  @collapseActions="!isDisabled && hideMobileAction()"
                />
              </td>
            </tr>

            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions slide-right"
            >
              <td colspan="10">
                <PlatformActions
                  :integration="row"
                  :disabled="isDisabled"
                  :integration-to-reset="integrationToReset"
                  @deleteIntegration="showDeleteModal"
                  @refreshIntegration="refreshIntegration"
                  @editIntegration="showEditForm(row.id)"
                  @toggleEnabled="toggleIntegrationEnabled"
                  @collapseActions="!isDisabled && hideMobileAction()"
                />
              </td>
            </tr>
            <tr
              v-if="isEditFormOpen(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <PlatformForm
                  :integration="row"
                  @closeForm="hideEditForm"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div v-if="integrations && integrations.length" class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="7"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'PLATFORMS_TABLE_LOADING'" />
    </div>
    <PlatformDeleteModal
      v-if="isDeleteModalOpen"
      :integration="integrationToDelete"
      :is-deleting="isDeletingIntegration"
      @closeModal="closeDeleteModal"
      @deleteIntegration="confirmDeleteIntegration"
    />
  </PortalWidget>
</template>

<script>
import { cloneDeep, debounce } from 'lodash';
import { utilities, integrationService, accountsService, oauthService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import { isStaging, isMobile } from '@/core/utilities';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import PortalWidget from '@/core/components/PortalWidget';
import PlatformForm from './PlatformForm';
import PlatformActions from './PlatformActions';
import PlatformDeleteModal from './PlatformDeleteModal';

export default {
  name: 'Platforms',
  components: {
    PortalWidget,
    PlatformForm,
    PlatformActions,
    PlatformDeleteModal
  },
  
  data() {
    return {
      isMobile: isMobile,
      activeActionRowId: null,
      currentPage: 1,
      totalPages: 0,
      isAddFormOpen: false,
      isDeleteModalOpen: false,
      activeEditRowId: null,
      integrationToDelete: null,
      integrationToReset: null,
      oauthCallbackWindow: null,
      showOauthRegisterLoader: false,
      authUrl: '',
      searchDebounce: debounce(this.handleSearch, 300),
      filters: {
        name: { 
          value: '', 
          keys: ['name', 'id'],
          transform: (value) => value.toLowerCase()
        }
      }
    };
  },
  beforeMount() {
    this.widget = {
      title:this.$t('PLATFORMS_TITLE'),
      iconClass: 'typcn typcn-group'
    };
  },
  created() {
    this.getIntegrations();
  },
  computed: {
    ...mapState('platforms', [
      'integrations'
    ]),
    ...mapGetters('platforms', [
      'getIntegrationsStatus',
      'updateIntegrationStatus',
      'deleteIntegrationStatus'
    ]),
    isLoading() {
      return this.getIntegrationsStatus.status == STATUS_PENDING;
    },
    isDisabled() {
      return (
        this.isAddFormOpen ||
        !!this.activeEditRowId
      );
    },
    isDeletingIntegration() {
      return this.deleteIntegrationStatus.status === STATUS_PENDING;
    },
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.isAddFormOpen = false;
    },
    'filters.name.value': {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.searchDebounce(newVal);
        this.hideEditForm();
        this.isAddFormOpen = false;
      }
    }
  },
  methods: {
    ...mapActions('platforms', [
      'getIntegrations',
      'updateIntegration',
      'deleteIntegration'
    ]),
    handleSearch(value) {
      this.filters.name.value = value;
      this.currentPage = 1; // Reset to first page on search
    },
    displayMobileAction(id) {
      this.activeActionRowId = id;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    showAddForm() {
      this.hideEditForm();
      this.isAddFormOpen = true;
    },
    showEditForm(id) {
      this.hideAddForm();
      this.activeEditRowId = id;
    },
    isEditFormOpen(id) {
      return !this.isAddFormOpen && this.activeEditRowId == id;
    },
    hideAddForm(newIntegration) {
      this.isAddFormOpen = false;
      if (newIntegration) {
        this.getIntegrations();
      }
    },
    hideEditForm() {
      this.activeEditRowId = null;
    },
    showDeleteModal(integration) {
      this.isDeleteModalOpen = true;
      this.integrationToDelete = integration;
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false;
      this.integrationToDelete = null;
    },
    async toggleIntegrationEnabled(integration) {
      const integrationCopy = cloneDeep(integration);
      integrationCopy.enabled = !integrationCopy.enabled;
      await this.updateIntegration(integrationCopy);
      if (this.updateIntegrationStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, 'Successfully ' + (integrationCopy.enabled ? 'enabled' : 'disabled') + ' integration');
      }
      if (this.updateIntegrationStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.updateIntegrationStatus.error);
      }
    },
    async confirmDeleteIntegration(integration) {
      await this.deleteIntegration(integration);
      if (this.deleteIntegrationStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('DELETE_PLATFORM_INTEGRATION_SUCCESSFUL_MESSAGE'));
        this.closeDeleteModal();
      }
      if (this.deleteIntegrationStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.deleteIntegrationStatus.error);
      }
    },
    async refreshIntegration(integration) {
      if (this.integrationToReset) return;

      try {
        this.integrationToReset = integration;
        this._RegisterOAuthMessageHandler();

        if (isStaging) {
          const response = await integrationService.getOauthMessageEvent();
          this._receiveOAuthMessage(response);
          return;
        }

        const authType = await integrationService.getIntegrationAuthType(this.integrationToReset.type);
        
        const response = await oauthService.getAuthUrl(accountsService.currentAccountId, authType);
        this.authUrl = response.data.authUrl;
        this.oauthCallbackWindow = window.open(this.authUrl);

      } catch (error) {
        utilities.errorHandler(error);
        this.cleanupOauth();
      }
    },
    cleanupOAuth() {
      this.integrationToReset = null;
      this._unregisterOAuthMessageHandler();
      this.showOauthRegisterLoader = false;
      
      if (this.oauthCallbackWindow) {
        this.oauthCallbackWindow.close();
        this.oauthCallbackWindow = null;
      }
    },
    transformPlatformType(type) {
      return integrationService.getPlatformLabel(type);
    },
    oauthWindowErrorCallback(error) {
      utilities.errorHandler(error.message);
      this.integrationToReset = null;
      this._UnregisterOAuthMessageHandler();
    },
    async oauthWindowSuccessCallback(authCode) {
      this.showOauthRegisterLoader = true;
      try {
        await integrationService.refreshToken(accountsService.currentAccountId, this.integrationToReset.id, { code: authCode, type: this.integrationToReset.type });
        utilities.showToaster(STATUS_SUCCESS, this.$t('PLATFORM_TOKEN_REFRESHED'));
      } catch (error) {
        utilities.errorHandler(error);
      } finally {
        this.showOauthRegisterLoader = false;
        this.integrationToReset = null;
        this._UnregisterOAuthMessageHandler();
      }
    },
    _RegisterOAuthMessageHandler() {
      window.addEventListener('message', this._receiveOAuthMessage, false);
    },
    _UnregisterOAuthMessageHandler() {
      window.removeEventListener('message', this._receiveOAuthMessage, false);
      this.oauthCallbackWindow = null;
    },
    _receiveOAuthMessage(ev) {
     const allowedOrigins = [
        'https://app.ringba.com',
        'https://offers.ringba.com',
        'http://localhost:9000'
      ];

      if ((!isStaging && !allowedOrigins.includes(ev.origin)) || ev.source !== this.oauthCallbackWindow) {
        return;
      }
      
      const msg = ev.data;
      
      if (!msg.success) {
        return this.oauthWindowErrorCallback(msg.error);
      }

      return this.oauthWindowSuccessCallback(msg.authCode);
    },
  },  
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>